/**
 * `category` query param accepted by service-types and service-price APIs.
 * 0 is a valid value (car), so callers must not truthy-check it.
 */
export enum SERVICE_CATEGORY {
  CAR = 0,
  BIKE = 1,
  SOFA = 2,
  TANK = 3,
  CARPET = 4,
}

export const SERVICE_CATEGORY_LABEL: { [key in SERVICE_CATEGORY]: string } = {
  [SERVICE_CATEGORY.CAR]: "Car",
  [SERVICE_CATEGORY.BIKE]: "Bike",
  [SERVICE_CATEGORY.SOFA]: "Sofa",
  [SERVICE_CATEGORY.TANK]: "Tank",
  [SERVICE_CATEGORY.CARPET]: "Carpet",
};

export const SERVICE_CATEGORY_SLUG: { [key in SERVICE_CATEGORY]: string } = {
  [SERVICE_CATEGORY.CAR]: "car",
  [SERVICE_CATEGORY.BIKE]: "bike",
  [SERVICE_CATEGORY.SOFA]: "sofa",
  [SERVICE_CATEGORY.TANK]: "tank",
  [SERVICE_CATEGORY.CARPET]: "carpet",
};

const SLUG_TO_SERVICE_CATEGORY: { [slug: string]: SERVICE_CATEGORY } = {
  car: SERVICE_CATEGORY.CAR,
  bike: SERVICE_CATEGORY.BIKE,
  sofa: SERVICE_CATEGORY.SOFA,
  tank: SERVICE_CATEGORY.TANK,
  carpet: SERVICE_CATEGORY.CARPET,
};

export const getServiceCategoryFromCategoryName = (
  categoryName: string
): SERVICE_CATEGORY => {
  const slug = `${categoryName || ""}`.toLowerCase().trim();
  if (SLUG_TO_SERVICE_CATEGORY[slug] !== undefined) {
    return SLUG_TO_SERVICE_CATEGORY[slug];
  }
  const firstWord = slug.split(/[\s_-]+/)[0];
  return SLUG_TO_SERVICE_CATEGORY[firstWord];
};

/** Query value for getCouponList `category` — only when a real category is selected. */
export const resolveCouponListCategory = (
  value?: string | number | null
): string | undefined => {
  if (value === 0 || value === "0") return `${SERVICE_CATEGORY.CAR}`;
  if (value === undefined || value === null || value === "" || value === "-1") {
    return undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}`;
  }
  const raw = `${value}`.trim();
  if (/^\d+$/.test(raw)) return raw;
  const fromName = getServiceCategoryFromCategoryName(raw);
  if (fromName !== undefined && fromName !== null) return `${fromName}`;
  const beforeSeparator = raw.split(/[:|/]/)[0].trim();
  if (beforeSeparator && beforeSeparator !== raw) {
    const fromPart = getServiceCategoryFromCategoryName(beforeSeparator);
    if (fromPart !== undefined && fromPart !== null) return `${fromPart}`;
  }
  return undefined;
};

/** Franchise user-details: category of purchased services on `orderInformation`. */
export const resolveOrderInformationCategory = (info: any): string | undefined => {
  if (!info || typeof info !== "object") return undefined;

  const direct = resolveCouponListCategory(
    info.category ??
      info.serviceCategory ??
      info.categoryLabel ??
      info.purchasedCategory ??
      info.service?.category ??
      info.service?.categoryLabel
  );
  if (direct) return direct;

  const collections = [
    info.ordersCount,
    info.orders,
    info.categoryBreakdown,
    info.purchasedCategories,
    info.categories,
  ].filter((item) => Array.isArray(item));

  for (const list of collections) {
    for (const item of list) {
      const resolved = resolveCouponListCategory(
        item?.category ??
          item?.serviceCategory ??
          item?.categoryLabel ??
          item?.serviceType ??
          item
      );
      if (resolved) return resolved;
    }
  }

  return undefined;
};

export const fromServiceCategorySlug = (
  slug?: string | null
): SERVICE_CATEGORY => {
  if (!slug) return SERVICE_CATEGORY.CAR;
  return SLUG_TO_SERVICE_CATEGORY[slug.toLowerCase()] ?? SERVICE_CATEGORY.CAR;
};

/** Resolves `/service-management/car` (and the same slug on pricing) to an enum. */
export const getServiceCategoryFromPathname = (
  pathname: string
): SERVICE_CATEGORY => {
  const segments = pathname.split("/").filter(Boolean);
  const slug = Object.values(SERVICE_CATEGORY_SLUG).find((value) =>
    segments.includes(value)
  );
  return fromServiceCategorySlug(slug);
};

/** Bike has no category-type listing, so categoryTypeId is omitted for it. */
export const hasCategoryTypeFilter = (category: SERVICE_CATEGORY | number) =>
  category === SERVICE_CATEGORY.CAR ||
  category === SERVICE_CATEGORY.SOFA ||
  category === SERVICE_CATEGORY.TANK ||
  category === SERVICE_CATEGORY.CARPET;

export const toServiceCategory = (
  value: SERVICE_CATEGORY | number | string | undefined
): SERVICE_CATEGORY => {
  const parsed = typeof value === "string" ? parseInt(value, 10) : value;
  if (
    parsed === SERVICE_CATEGORY.BIKE ||
    parsed === SERVICE_CATEGORY.SOFA ||
    parsed === SERVICE_CATEGORY.TANK ||
    parsed === SERVICE_CATEGORY.CARPET
  ) {
    return parsed;
  }
  return SERVICE_CATEGORY.CAR;
};
